/**
 * iOS bookmark icon & label injection for the add-to-home-screen feature.
 * 「保存到桌面」的 iOS 书签图标与名字注入。
 *
 * iOS deliberately gets NO manifest (standalone would isolate storage and lose
 * the whole vuex-persist state), so the saved bookmark reads two things from
 * the DOM instead: the icon from `<link rel="apple-touch-icon">` and the label
 * from `<meta name="apple-mobile-web-app-title">` (falling back to the
 * uncontrolled document.title). index.html ships a static platform icon and no
 * title meta — without this injection every venue saves as the generic
 * MondoMenu icon with a marketing-string name.
 *
 * iOS 刻意不下发 manifest（standalone 会隔离存储、丢光 vuex-persist 状态），
 * 书签改从 DOM 取两样东西：图标读 `<link rel="apple-touch-icon">`，名字读
 * `<meta name="apple-mobile-web-app-title">`（缺省回落不可控的 document.title）。
 * index.html 里是写死的平台图标且没有 title meta——不注入的话，每家店存下来
 * 都是 MondoMenu 通用图标 + 一串营销语名字。
 *
 * Only ever applied on iOS Safari; Android gets both from the manifest.
 * 只在 iOS Safari 注入；Android 两样都由 manifest 提供。
 */

import { detectA2hsPlatform, A2HS_PLATFORM } from './platform'

export const IOS_TITLE_META_ID = 'a2hs-ios-title'

// 记住被我们改写的静态 link 原值，换店/清理时还原
let patchedLink = null
let originalHref = null
let originalSizes = null

/**
 * Build the backend-derived venue icon URL (192px opaque square PNG — Safari
 * scales it to 180 itself). 拼接后端派生的店铺图标地址（192 不透明方图，
 * Safari 自己缩到 180）。
 *
 * @param {string} venueId
 * @param {string} [apiBase] - defaults to VUE_APP_BASE_API
 * @returns {string} the URL, or '' when unusable
 */
export function buildVenueIconUrl(venueId, apiBase = process.env.VUE_APP_BASE_API) {
  if (!venueId || !apiBase) return ''
  return `${apiBase}/customer/venue/icon?id=${encodeURIComponent(venueId)}&size=192`
}

/**
 * The label under the saved icon — pure. Venue-provided short name wins; the
 * fallback is the FULL venue name, untruncated — product call (2026-07-28):
 * let iOS ellipsise natively instead of any server/client-side cut. Matches
 * the backend manifest short_name contract for Android.
 * 图标下方的名字——纯函数。店家自配短名优先；回落给**全名不截断**——产品
 * 拍板（2026-07-28）：超长交给 iOS 原生省略号，不做任何端上截断。与后端
 * manifest 给 Android 的 short_name 契约一致。
 *
 * @param {string} homeScreenName - venue-provided short name ('' when unset)
 * @param {string} venueName - full venue name
 * @returns {string}
 */
export function shortBookmarkName(homeScreenName, venueName) {
  const short = typeof homeScreenName === 'string' ? homeScreenName.trim() : ''
  if (short) return short
  return typeof venueName === 'string' ? venueName.trim() : ''
}

/**
 * Inject (or update) the venue's bookmark icon + label — iOS Safari only.
 * 注入（或更新）店铺书签图标与名字——只在 iOS Safari。
 *
 * @param {Object} venue
 * @param {string} venue.venueId
 * @param {string} [venue.venueName]
 * @param {string} [venue.homeScreenName]
 * @param {Object} [options]
 * @param {string} [options.userAgent] - injectable for tests / 测试用注入
 * @param {string} [options.apiBase] - injectable for tests / 测试用注入
 * @returns {boolean} whether the meta is now in place / 是否已就位
 */
export function applyIosBookmarkMeta({ venueId, venueName, homeScreenName } = {}, { userAgent, apiBase } = {}) {
  if (typeof document === 'undefined') return false

  const ua = userAgent !== undefined
    ? userAgent
    : (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  if (detectA2hsPlatform(ua) !== A2HS_PLATFORM.IOS_SAFARI) return false

  const iconUrl = buildVenueIconUrl(venueId, apiBase)
  if (!iconUrl) return false

  // 图标：删掉旧 link、插入全新节点。两个原因：① 不并存新旧两条——Safari 按
  // sizes 挑「更接近 180」的那条，静态 180 通用图标会赢过店铺 192 图；
  // ② 原地改 href 的感知不可靠（真机出现过保存到旧图标），换节点才稳定触发重读
  const existing = document.querySelector('link[rel="apple-touch-icon"]')
  if (existing) {
    // 只在第一次替换时记录静态原值；后续换店重复注入时 existing 已是我们的节点
    if (patchedLink !== existing) {
      originalHref = existing.getAttribute('href')
      originalSizes = existing.getAttribute('sizes')
    }
    existing.parentNode.removeChild(existing)
  } else if (!patchedLink) {
    originalHref = null
    originalSizes = null
  }
  const link = document.createElement('link')
  link.rel = 'apple-touch-icon'
  link.setAttribute('sizes', '192x192')
  link.setAttribute('href', iconUrl)
  document.head.appendChild(link)
  patchedLink = link

  // 名字：upsert 我们自己的 meta
  let meta = document.getElementById(IOS_TITLE_META_ID)
  if (!meta) {
    meta = document.createElement('meta')
    meta.id = IOS_TITLE_META_ID
    meta.setAttribute('name', 'apple-mobile-web-app-title')
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', shortBookmarkName(homeScreenName, venueName))
  return true
}

/**
 * Restore the static icon and drop the label meta (venue switch / teardown).
 * 还原静态图标、移除名字 meta（换店/清理）。
 */
export function removeIosBookmarkMeta() {
  if (typeof document === 'undefined') return
  if (patchedLink) {
    if (originalHref === null) {
      if (patchedLink.parentNode) patchedLink.parentNode.removeChild(patchedLink)
    } else {
      patchedLink.setAttribute('href', originalHref)
      if (originalSizes === null) patchedLink.removeAttribute('sizes')
      else patchedLink.setAttribute('sizes', originalSizes)
    }
    patchedLink = null
    originalHref = null
    originalSizes = null
  }
  const meta = document.getElementById(IOS_TITLE_META_ID)
  if (meta && meta.parentNode) meta.parentNode.removeChild(meta)
}
